/* eslint-disable quotes */

import { UntypedFormBuilder } from '@angular/forms';

import { checkIsNAN, formatNumber } from '../../../shared/utils/number.util';
import {
  CombinedHousingExpenseModel,
  HousingExpenseDetailModel,
  HousingExpenseModel,
} from '../../../cmg-1003/models/employment-income-expense.model';
import { PrepaidItemType } from '../../../cmg-1003/models/lookups/expenses.lookups';

/** (Mostly copied from Clear 1.) Calculates HCLTV for Subordinate Financing. */
export const calculateHCLTV = (helocCreditLimit: number, loanAmount: number, appraisalValue: number): number => {
  const helocCreditAmount = checkIsNAN(Number(helocCreditLimit));
  const baseLoanAmount = checkIsNAN(Number(loanAmount));
  const appraisedValue = checkIsNAN(Number(appraisalValue));

  if (helocCreditAmount >= 0 && !!appraisedValue && appraisedValue !== 0) {
    return ((helocCreditAmount + baseLoanAmount) / appraisedValue) * 100;
  }
  return 0;
};

// TODO: Consolidate with loan-info's calculatePI(): https://cmgfi.atlassian.net/browse/URLA2-412
/**  (Mostly copied from Clear 1.) Calculates "Calculated P & I" for Subordinate Financing. */
export const calculatePI = (loanAmount?: number, interestRate?: number, loanTerm?: number, isInterestOnly?: boolean): number => {
  if (interestRate === undefined || !loanTerm) {
    return null;
  }
  const monthlyInterestRate = interestRate / 100 / 12;
  const loanTermMultiplier = Math.pow(1 + monthlyInterestRate, loanTerm ?? 0);

  if (monthlyInterestRate === 0) {
    return loanTerm === 0 ? 0 : formatNumber(loanAmount / loanTerm);
  }

  if (isInterestOnly) {
    return formatNumber(monthlyInterestRate * loanAmount);
  }

  return formatNumber(((monthlyInterestRate * loanTermMultiplier) / (loanTermMultiplier - 1)) * loanAmount);
};

export const calculateProposedPropertyTax = (salesPrice?: number, factor?: number): number =>
  !salesPrice || !factor ? null : (salesPrice * ((factor ?? 0) / 100)) / 12;

export const calculateProposedPropertyTaxFactor = (salesPrice?: number, amount?: number): number =>
  !salesPrice || !amount ? null : Number((((amount ?? 0) * 12 * 100) / salesPrice).toFixed(3));

/** Returns the default values for a new expense - i.e. NOT "Other Expenses". */
export const createNewExpense = (defaultExpenseType?: PrepaidItemType) => {
  return <HousingExpenseDetailModel>{
    presentAmount: null,
    expenseType: defaultExpenseType ?? null,
    proposedAmount: null,
    prepaidItemId: null,
    premiumPaidToType: null,
    factor: null,
  };
};

/** Returns the default values for a new "Other Expense" (added via UI). */
export const createNewOtherExpense = (defaultExpenseType?: PrepaidItemType) => {
  return <HousingExpenseDetailModel>{
    expenseType: defaultExpenseType ?? PrepaidItemType.NotAssigned,
    proposedAmount: null,
    prepaidItemId: null,
    premiumPaidToType: null,
    factor: null,
  };
};

/** Returns a FormGroup for "Other Expenses". */
export const createOtherExpenseFormGroup = (fb: UntypedFormBuilder) => {
  return fb.group({
    expenseType: [],
    proposedAmount: [],
    prepaidItemId: [],
    premiumPaidToType: [],
    factor: [], // Used for checkbox for Other Expense - "Supplemental Expense (2)" only
  });
};

/**
 * Finds the subset of 'otherHousingExpenses' array that should be visible in table. Expenses should be either:
 *
 * (1) Fully complete (i.e. has valid expenseType, proposedAmount)
 *
 * (2) Not yet in Byte (i.e. has negative prepaidItem)
 *
 * (3) expenseType === PrepaidItemType.NotAssigned
 */
export const findVisibleOtherExpenses = (otherExpenses: HousingExpenseModel[]) =>
  otherExpenses?.filter(
    (othExp) =>
      (othExp.expenseType !== PrepaidItemType.NotAssigned && !!othExp.proposedAmount) ||
      (othExp.prepaidItemId < 0 &&
        ![PrepaidItemType.Line1003, PrepaidItemType.FloodInsurance, PrepaidItemType.Line1007].includes(othExp.expenseType)) ||
      othExp.expenseType === PrepaidItemType.NotAssigned,
  );

/** Creates a new CombinedHousingExpenses object with default values, usually for "Add New Application". */
export const initializeCombinedHousingExpenses = () => {
  return <CombinedHousingExpenseModel>{
    isIncludedInPiti: false,
    presentRent: null,
    firstMortgage: createNewExpense(),
    hazardInsurance: createNewExpense(PrepaidItemType.HazardInsurance),
    homeOwnerAssnDues: createNewExpense(PrepaidItemType.HOD),
    mortgageInsurance: createNewExpense(PrepaidItemType.MortgageInsurance),
    otherHousingExpenses: [],
    otherMortgage: createNewExpense(),
    othersTotal: createNewExpense(),
    propertyInsurance: createNewExpense(),
    realEstateTaxes: createNewExpense(PrepaidItemType.PropertyTaxes),
    total: createNewExpense(),
  };
};
